/**
 * Utilitaires de validation diversité appareils/IP
 * Corrige les feedbacks Viguen sur les faux positifs device/IP
 */

interface DeviceValidationResult {
  score: number; // 0-100
  isValid: boolean; // >= 70
  issues: string[];
  details: {
    totalReferents: number;
    uniqueDevices: number;
    uniqueIPs: number;
    candidateDeviceMatch: boolean;
    candidateIPMatch: boolean;
    diversityRatio: number;
  };
}

interface ReferentData {
  id: string;
  name: string;
  email?: string; // ✅ NOUVEAU : Ajout de l'email pour la détection de fraude
  deviceFingerprint?: string;
  ipAddress?: string;
  // Nouvelles propriétés pour la collaboration candidat
  candidate_collaboration_start_date?: string;
  candidate_collaboration_end_date?: string;
  candidate_still_working_together?: boolean;
}

interface CandidateData {
  deviceFingerprint?: string;
  ipAddress?: string;
  email?: string; // ✅ NOUVEAU : Ajout de l'email candidat pour détecter les similitudes
}

/**
 * Calcule le score de diversité des appareils (corrigé)
 * Résout le problème : "1 référent = 100% automatique"
 */
export function calculateDeviceDiversityScore(
  referents: ReferentData[],
  candidateData?: CandidateData
): DeviceValidationResult {
  const issues: string[] = [];
  let score = 100;
  
  // Filtrer les référents avec device fingerprint
  const referentsWithDevice = referents.filter(r => r.deviceFingerprint);
  const totalReferents = referents.length;
  
  if (totalReferents === 0) {
    return {
      score: 0,
      isValid: false,
      issues: ['Aucun référent trouvé'],
      details: {
        totalReferents: 0,
        uniqueDevices: 0,
        uniqueIPs: 0,
        candidateDeviceMatch: false,
        candidateIPMatch: false,
        diversityRatio: 0
      }
    };
  }

  if (referentsWithDevice.length === 0) {
    return {
      score: 0,
      isValid: false,
      issues: ['Aucune empreinte d\'appareil disponible'],
      details: {
        totalReferents,
        uniqueDevices: 0,
        uniqueIPs: 0,
        candidateDeviceMatch: false,
        candidateIPMatch: false,
        diversityRatio: 0
      }
    };
  }

  // Extraire les device fingerprints uniques
  const deviceFingerprints = referentsWithDevice.map(r => r.deviceFingerprint!);
  const uniqueDevices = new Set(deviceFingerprints);
  const uniqueDeviceCount = uniqueDevices.size;

  // CORRECTION MAJEURE : Vérifier si même appareil que le candidat
  let candidateDeviceMatch = false;
  if (candidateData?.deviceFingerprint) {
    candidateDeviceMatch = deviceFingerprints.includes(candidateData.deviceFingerprint);
    
    if (candidateDeviceMatch) {
      score = 0; // Score automatique 0% si même appareil que candidat
      issues.push('Appareil identique détecté entre candidat et référent(s)');
    }
  }

  // Si pas de match candidat, calculer diversité normale
  if (!candidateDeviceMatch) {
    const diversityRatio = uniqueDeviceCount / referentsWithDevice.length;
    
    // CORRECTION : Logique stricte pour diversité
    if (referentsWithDevice.length === 1) {
      // 1 seul référent : OK si pas même appareil que candidat
      score = candidateData?.deviceFingerprint ? 85 : 90;
    } else if (referentsWithDevice.length === 2) {
      // 2 référents : doivent avoir 2 appareils différents
      if (uniqueDeviceCount === 2) {
        score = 95;
      } else {
        score = 30; // Même appareil entre référents
        issues.push('Les référents utilisent le même appareil');
      }
    } else {
      // 3+ référents : au moins 70% d'appareils uniques
      if (diversityRatio >= 0.8) {
        score = 95;
      } else if (diversityRatio >= 0.6) {
        score = 80;
      } else if (diversityRatio >= 0.4) {
        score = 60;
        issues.push(`Diversité d'appareils insuffisante (${Math.round(diversityRatio * 100)}%)`);
      } else {
        score = 20;
        issues.push(`Diversité d'appareils très faible (${Math.round(diversityRatio * 100)}%)`);
      }
    }
  }

  return {
    score,
    isValid: score >= 70,
    issues,
    details: {
      totalReferents,
      uniqueDevices: uniqueDeviceCount,
      uniqueIPs: 0, // Calculé séparément
      candidateDeviceMatch,
      candidateIPMatch: false, // Calculé séparément
      diversityRatio: uniqueDeviceCount / Math.max(referentsWithDevice.length, 1)
    }
  };
}

/**
 * Détermine si une IP identique entre candidat et référent est justifiée
 * Logique simple : UNIQUEMENT si ils travaillent encore ensemble
 */
function shouldAllowSameIP(
  referent: ReferentData, 
  candidateData: CandidateData
): boolean {
  // Si ils travaillent ENCORE ensemble → IP identique est OK
  if (referent.candidate_still_working_together === true) {
    // Logging pour monitoring
    console.log('[IP Check] Same IP allowed:', {
      referentName: referent.name,
      stillWorking: referent.candidate_still_working_together,
      decision: 'ALLOWED ✅'
    });
    return true;
  }
  
  // Tous les autres cas → IP identique = suspect
  console.log('[IP Check] Same IP blocked:', {
    referentName: referent.name,
    stillWorking: referent.candidate_still_working_together,
    endDate: referent.candidate_collaboration_end_date,
    decision: 'BLOCKED ⚠️'
  });
  return false;
}

/**
 * Calcule le score de diversité des IP (corrigé avec logique de collaboration)
 * Intègre la logique métier : IP identique OK si collaboration en cours
 */
export function calculateIPDiversityScore(
  referents: ReferentData[],
  candidateData?: CandidateData
): DeviceValidationResult {
  const issues: string[] = [];
  let score = 100;
  
  // Filtrer les référents avec IP
  const referentsWithIP = referents.filter(r => r.ipAddress);
  const totalReferents = referents.length;
  
  if (totalReferents === 0) {
    return {
      score: 0,
      isValid: false,
      issues: ['Aucun référent trouvé'],
      details: {
        totalReferents: 0,
        uniqueDevices: 0,
        uniqueIPs: 0,
        candidateDeviceMatch: false,
        candidateIPMatch: false,
        diversityRatio: 0
      }
    };
  }

  if (referentsWithIP.length === 0) {
    return {
      score: 0,
      isValid: false,
      issues: ['Aucune adresse IP disponible'],
      details: {
        totalReferents,
        uniqueDevices: 0,
        uniqueIPs: 0,
        candidateDeviceMatch: false,
        candidateIPMatch: false,
        diversityRatio: 0
      }
    };
  }

  // Extraire les IP uniques
  const ipAddresses = referentsWithIP.map(r => r.ipAddress!);
  const uniqueIPs = new Set(ipAddresses);
  const uniqueIPCount = uniqueIPs.size;

  // CORRECTION MAJEURE : Vérifier si même IP que le candidat avec logique de collaboration
  let candidateIPMatch = false;
  if (candidateData?.ipAddress) {
    const referentsWithSameIP = referentsWithIP.filter(r => 
      r.ipAddress === candidateData.ipAddress
    );
    
    if (referentsWithSameIP.length > 0) {
      // Vérifier si l'IP identique est justifiée par la collaboration
      const legitimateMatches = referentsWithSameIP.filter(r => 
        shouldAllowSameIP(r, candidateData)
      );
      
      const suspiciousMatches = referentsWithSameIP.filter(r => 
        !shouldAllowSameIP(r, candidateData)
      );
      
      if (suspiciousMatches.length > 0) {
        // IP identique sans justification = fraude
        score = 0;
        candidateIPMatch = true;
        issues.push(`⚠️ Adresse IP identique suspecte détectée (${suspiciousMatches.length} référent(s) sans collaboration en cours)`);
      } else if (legitimateMatches.length > 0) {
        // IP identique mais justifiée = score normal
        score = 95; // Pas de pénalité
        candidateIPMatch = false; // Considéré comme légitime
        issues.push(`ℹ️ Adresse IP identique détectée - Justifiée par collaboration en cours (${legitimateMatches.length} référent(s))`);
      }
    }
  }

  // Si pas de match candidat, calculer diversité normale
  if (!candidateIPMatch) {
    const diversityRatio = uniqueIPCount / referentsWithIP.length;
    
    // CORRECTION : Logique stricte pour diversité
    if (referentsWithIP.length === 1) {
      // 1 seul référent : OK si pas même IP que candidat
      score = candidateData?.ipAddress ? 85 : 90;
    } else if (referentsWithIP.length === 2) {
      // 2 référents : doivent avoir 2 IP différentes
      if (uniqueIPCount === 2) {
        score = 95;
      } else {
        score = 30; // Même IP entre référents
        issues.push('Les référents utilisent la même adresse IP');
      }
    } else {
      // 3+ référents : au moins 70% d'IP uniques
      if (diversityRatio >= 0.8) {
        score = 95;
      } else if (diversityRatio >= 0.6) {
        score = 80;
      } else if (diversityRatio >= 0.4) {
        score = 60;
        issues.push(`Diversité d'IP insuffisante (${Math.round(diversityRatio * 100)}%)`);
      } else {
        score = 20;
        issues.push(`Diversité d'IP très faible (${Math.round(diversityRatio * 100)}%)`);
      }
    }
  }

  return {
    score,
    isValid: score >= 70,
    issues,
    details: {
      totalReferents,
      uniqueDevices: 0, // Calculé séparément
      uniqueIPs: uniqueIPCount,
      candidateDeviceMatch: false, // Calculé séparément
      candidateIPMatch,
      diversityRatio: uniqueIPCount / Math.max(referentsWithIP.length, 1)
    }
  };
}

/**
 * Validation combinée device + IP
 */
export function validateDeviceAndIPDiversity(
  referents: ReferentData[],
  candidateData?: CandidateData
): {
  deviceValidation: DeviceValidationResult;
  ipValidation: DeviceValidationResult;
  overallScore: number;
  overallValid: boolean;
  criticalIssues: string[];
} {
  const deviceValidation = calculateDeviceDiversityScore(referents, candidateData);
  const ipValidation = calculateIPDiversityScore(referents, candidateData);
  
  // Score global (moyenne pondérée)
  const overallScore = Math.round((deviceValidation.score + ipValidation.score) / 2);
  const overallValid = overallScore >= 70;
  
  // Issues critiques (score 0)
  const criticalIssues: string[] = [];
  if (deviceValidation.score === 0) {
    criticalIssues.push(...deviceValidation.issues);
  }
  if (ipValidation.score === 0) {
    criticalIssues.push(...ipValidation.issues);
  }
  
  return {
    deviceValidation,
    ipValidation,
    overallScore,
    overallValid,
    criticalIssues
  };
}

/**
 * Détecte les patterns de fraude potentielle (mis à jour avec logique de collaboration + emails frauduleux)
 */
export function detectFraudPatterns(
  referents: ReferentData[],
  candidateData?: CandidateData
): {
  isSuspicious: boolean;
  suspicionLevel: 'low' | 'medium' | 'high';
  reasons: string[];
} {
  const reasons: string[] = [];
  let suspicionLevel: 'low' | 'medium' | 'high' = 'low';
  
  const validation = validateDeviceAndIPDiversity(referents, candidateData);
  
  // ✅ NOUVEAU : Détection d'emails frauduleux (priorité absolue)
  const fraudulentEmails = referents.filter(r => {
    if (!r.email) return false;
    
    const domain = r.email.split('@')[1]?.toLowerCase();
    if (!domain) return false;
    
    // 1. Vérifier domaines non professionnels
    const personalDomains = [
      // Grands fournisseurs internationaux
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com',
      'live.com', 'icloud.com', 'aol.com', 'protonmail.com',
      'mail.com', 'gmx.com', 'zoho.com', 'yandex.com',
      
      // Français
      'free.fr', 'orange.fr', 'sfr.fr', 'wanadoo.fr', 'laposte.net',
      'bbox.fr', 'numericable.fr', 'club-internet.fr', 'cegetel.net',
      
      // Autres pays européens
      'web.de', 'gmx.de', 't-online.de', 'freenet.de',
      'libero.it', 'virgilio.it', 'tiscali.it',
      'mail.ru', 'yandex.ru', 'rambler.ru',
      
      // Asie
      'qq.com', '163.com', '126.com', 'sina.com',
      'naver.com', 'daum.net', 'hanmail.net'
    ];

    const disposableDomains = [
      'mailinator.com', 'guerrillamail.com', 'temp-mail.org',
      'yopmail.com', '10minutemail.com', 'throwaway.email',
      'maildrop.cc', 'sharklasers.com', 'trashmail.com',
      'tempmail.net', 'getnada.com', 'mohmal.com',
      'mailcatch.com', 'mailnesia.com', 'spamgourmet.com'
    ];
    
    // Domaine personnel ou jetable = frauduleux
    if (personalDomains.includes(domain) || disposableDomains.includes(domain)) {
      return true;
    }
    
    // 2. ✅ NOUVEAU : Vérifier cohérence nom/email (même avec domaine professionnel)
    if (r.name) {
      const emailLocal = r.email.split('@')[0].toLowerCase();
      const nameParts = r.name.toLowerCase().trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      // Normaliser pour la comparaison (supprimer accents, caractères spéciaux)
      const normalizeString = (str) => {
        return str
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '') // Supprimer accents
          .replace(/[^a-z0-9]/g, ''); // Garder seulement lettres et chiffres
      };
      
      const normalizedEmailLocal = normalizeString(emailLocal);
      const normalizedFirstName = normalizeString(firstName);
      const normalizedLastName = normalizeString(lastName);
      
      // Vérifier si le prénom OU le nom apparaît dans l'email
      const hasFirstName = normalizedFirstName.length > 2 && normalizedEmailLocal.includes(normalizedFirstName);
      const hasLastName = normalizedLastName.length > 2 && normalizedEmailLocal.includes(normalizedLastName);
      
      // Si ni prénom ni nom ne correspondent = FRAUDULEUX
      if (!hasFirstName && !hasLastName) {
        return true;
      }
    }
    
    return false;
  });
  
  // ✅ Email frauduleux = FRAUDE ÉLIMINATOIRE (niveau HIGH)
  if (fraudulentEmails.length > 0) {
    suspicionLevel = 'high';
    const emailList = fraudulentEmails.map(r => r.email).join(', ');
    reasons.push(`Email(s) frauduleux détecté(s): ${emailList}`);
  }
  
  // ✅ NOUVEAU : Détecter similarité email référent ↔ candidat (même nom)
  if (candidateData?.email) {
    const candidateEmailLocal = candidateData.email.split('@')[0]?.toLowerCase();
    if (candidateEmailLocal) {
      const similarEmails = referents.filter(r => {
        if (!r.email) return false;
        const referentEmailLocal = r.email.split('@')[0]?.toLowerCase();
        
        // Vérifier si les parties locales sont identiques ou très similaires
        if (referentEmailLocal === candidateEmailLocal) {
          return true;
        }
        
        // Vérifier similarité avec variations courantes (points, tirets, etc.)
        const normalizeEmailLocal = (email) => email.replace(/[.-_]/g, '');
        const normalizedCandidate = normalizeEmailLocal(candidateEmailLocal);
        const normalizedReferent = normalizeEmailLocal(referentEmailLocal);
        
        return normalizedCandidate === normalizedReferent;
      });
      
      if (similarEmails.length > 0) {
        suspicionLevel = 'high';
        const similarEmailList = similarEmails.map(r => r.email).join(', ');
        reasons.push(`Email(s) référent similaire(s) au candidat: ${similarEmailList}`);
      }
    }
  }
  
  // Fraude évidente : même device ET même IP que candidat (non justifiée)
  if (validation.deviceValidation.details.candidateDeviceMatch && 
      validation.ipValidation.details.candidateIPMatch) {
    suspicionLevel = 'high';
    reasons.push('Même appareil ET même IP que le candidat');
  }
  // Fraude probable : même device que candidat
  else if (validation.deviceValidation.details.candidateDeviceMatch) {
    suspicionLevel = 'high';
    reasons.push('Même appareil que le candidat');
  }
  // Fraude probable : même IP que candidat (non justifiée par collaboration)
  else if (validation.ipValidation.details.candidateIPMatch) {
    // Vérifier si c'est justifié par la collaboration
    const referentsWithSameIP = referents.filter(r => 
      r.ipAddress === candidateData?.ipAddress
    );
    const unjustifiedMatches = referentsWithSameIP.filter(r => 
      !shouldAllowSameIP(r, candidateData!)
    );
    
    if (unjustifiedMatches.length > 0) {
      suspicionLevel = 'high';
      reasons.push('Même adresse IP que le candidat sans collaboration en cours');
    }
  }
  // Suspicion : tous les référents même device/IP entre eux
  else if (referents.length > 1) {
    const deviceFingerprints = referents.map(r => r.deviceFingerprint).filter(Boolean);
    const ipAddresses = referents.map(r => r.ipAddress).filter(Boolean);
    
    if (deviceFingerprints.length > 1 && new Set(deviceFingerprints).size === 1) {
      suspicionLevel = suspicionLevel === 'low' ? 'medium' : 'high';
      reasons.push('Tous les référents utilisent le même appareil');
    }
    
    if (ipAddresses.length > 1 && new Set(ipAddresses).size === 1) {
      suspicionLevel = suspicionLevel === 'low' ? 'medium' : 'high';
      reasons.push('Tous les référents utilisent la même adresse IP');
    }
  }
  
  return {
    isSuspicious: suspicionLevel !== 'low',
    suspicionLevel,
    reasons
  };
}